import { useCallback, useEffect, useRef, useState } from "react";
import { getDownloadURL, getStorage, ref as storageRef } from "firebase/storage";

const DEFAULT_HERO_IMAGE_PATHS = {
  alert: "Podcast_images/bovia/alert.jpg",
  blocked_road: "Podcast_images/bovia/blocked_road.jpg",
  blocked_road_eq: "Podcast_images/demos/eq_blockage.jpg",
  neighbour_buddy: "Podcast_images/bovia/neighbour_buddy.jpg",
  go_bag: "Podcast_images/bovia/go_bag.jpg",
  firefighter_congestion: "Podcast_images/bovia/firefighter_congestion.jpg",
  car_abandon_accident: "Podcast_images/bovia/car_abandon_accident.jpg",
  traffic_congestion: "Podcast_images/bovia/traffic_congestion.jpg",
  leave_early: "Podcast_images/bovia/leave_early.jpg",
  know_your_zone: "Podcast_images/bovia/know_your_zone.jpg",
  earthquake_fault_line: "Podcast_images/demos/earthquake_hayward_fault.jpg",
  earthquake_damage: "Podcast_images/demos/earthquake_damage.jpg",
  flood_blockage: "Podcast_images/demos/flood_blockage.jpg",
  floods_damage: "Podcast_images/demos/flood_damage.jpg"
};

const resolveVariableUrl = async ({ storage, meta }) => {
  const storagePath = String(meta?.storagePath || "");
  if (storagePath) {
    return getDownloadURL(storageRef(storage, storagePath)).catch(() => meta?.url || "");
  }
  return meta?.url || "";
};

const normalizeVariableMap = (raw) => (
  raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {}
);

const useHeroSceneMedia = ({ podcastConfig, isPodcastPlaying }) => {
  const [heroImages, setHeroImages] = useState({});
  const [heroVideos, setHeroVideos] = useState({});
  const [activeHeroUrl, setActiveHeroUrl] = useState(null);
  const [activeHeroVideo, setActiveHeroVideo] = useState(null);
  const [heroVisible, setHeroVisible] = useState(false);
  const [heroAnim, setHeroAnim] = useState("");
  const activeHeroVideoRef = useRef(null);
  const heroVideoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const storage = getStorage();
    const customImages = normalizeVariableMap(podcastConfig?.image_variables);
    const customVideos = normalizeVariableMap(podcastConfig?.video_variables);

    const load = async () => {
      const defaultImageEntries = await Promise.all(
        Object.entries(DEFAULT_HERO_IMAGE_PATHS).map(async ([key, path]) => {
          try {
            const url = await getDownloadURL(storageRef(storage, path));
            return [key, url];
          } catch (err) {
            console.error("Failed to load hero image:", key, err);
            return [key, null];
          }
        })
      );

      const imageMap = {};
      defaultImageEntries.forEach(([key, url]) => {
        if (url) imageMap[key] = url;
      });

      const customImageEntries = await Promise.all(
        Object.entries(customImages).map(async ([key, meta]) => [
          String(key),
          await resolveVariableUrl({ storage, meta })
        ])
      );
      customImageEntries.forEach(([key, url]) => {
        if (key && url) imageMap[key] = url;
      });

      const customVideoEntries = await Promise.all(
        Object.entries(customVideos).map(async ([key, meta]) => [
          String(key),
          await resolveVariableUrl({ storage, meta })
        ])
      );
      const videoMap = {};
      customVideoEntries.forEach(([key, url]) => {
        if (key && url) videoMap[key] = url;
      });

      if (!cancelled) {
        setHeroImages(imageMap);
        setHeroVideos(videoMap);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [podcastConfig]);

  useEffect(() => {
    const hasHeroMedia = Boolean(activeHeroUrl || activeHeroVideo?.url);
    if (hasHeroMedia) {
      setHeroVisible(true);
      setHeroAnim("hero-roll-in");
    } else if (heroVisible) {
      setHeroAnim("hero-roll-out");
    }
  }, [activeHeroUrl, activeHeroVideo?.url, heroVisible]);

  useEffect(() => {
    activeHeroVideoRef.current = activeHeroVideo;
  }, [activeHeroVideo]);

  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video || !activeHeroVideo?.url) return;
    const offset = Math.max(0, Number(activeHeroVideo.offset) || 0);
    try {
      if (Math.abs((video.currentTime || 0) - offset) > 0.35) {
        video.currentTime = offset;
      }
    } catch {}
    if (isPodcastPlaying) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [activeHeroVideo?.url, activeHeroVideo?.sceneStart, activeHeroVideo?.offset, isPodcastPlaying]);

  const clearHeroMedia = useCallback(() => {
    setActiveHeroUrl(null);
    setActiveHeroVideo(null);
  }, []);

  const syncHeroVideoForScene = useCallback((scene, tSec, key) => {
    const active = activeHeroVideoRef.current;
    const video = heroVideoRef.current;
    if (!video || !active || active.key !== key) return;

    const sceneStart = Number(scene?.start) || 0;
    const sceneEnd = Number(scene?.end);
    const maxOffset = Number.isFinite(sceneEnd) && sceneEnd > sceneStart
      ? sceneEnd - sceneStart
      : Number.POSITIVE_INFINITY;
    const desiredTime = Math.max(0, Math.min(Number(tSec) - sceneStart, maxOffset));

    try {
      if (Number.isFinite(desiredTime) && Math.abs((video.currentTime || 0) - desiredTime) > 0.5) {
        video.currentTime = desiredTime;
      }
    } catch {}

    if (isPodcastPlaying && video.paused) {
      video.play().catch(() => {});
    }
  }, [isPodcastPlaying]);

  const applyHeroMediaForScene = useCallback((scene, tSec) => {
    if (scene?.showHeroVideo?.videoName) {
      const key = String(scene.showHeroVideo.videoName);
      const url = heroVideos[key];
      const fallbackImageKey = scene?.showHeroImage?.imageName
        ? String(scene.showHeroImage.imageName)
        : "";
      setActiveHeroUrl(fallbackImageKey ? (heroImages[fallbackImageKey] || null) : null);
      if (url) {
        const sceneStart = Number(scene.start) || 0;
        const offset = Math.max(0, Number(tSec) - sceneStart);
        const loop = Boolean(scene.showHeroVideo.loop);
        setActiveHeroVideo((prev) => (
          prev?.key === key &&
          prev?.url === url &&
          prev?.sceneStart === sceneStart &&
          prev?.loop === loop
            ? prev
            : { key, url, sceneStart, offset, loop }
        ));
        syncHeroVideoForScene(scene, tSec, key);
      } else {
        setActiveHeroVideo(null);
      }
      return;
    }

    if (scene?.showHeroImage?.imageName) {
      const key = String(scene.showHeroImage.imageName);
      const url = heroImages[key];
      setActiveHeroUrl(url || null);
      setActiveHeroVideo(null);
      return;
    }

    clearHeroMedia();
  }, [clearHeroMedia, heroImages, heroVideos, syncHeroVideoForScene]);

  const handleHeroAnimationEnd = useCallback(() => {
    if (heroAnim === "hero-roll-out") {
      setHeroVisible(false);
      setHeroAnim("");
    }
  }, [heroAnim]);

  return {
    activeHeroUrl,
    activeHeroVideo,
    heroAnim,
    heroVideoRef,
    heroVisible,
    applyHeroMediaForScene,
    clearHeroMedia,
    handleHeroAnimationEnd
  };
};

export default useHeroSceneMedia;
